import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Menu,
  CircularProgress,
  Switch,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { 
  ArrowLeft, 
  Plus, 
  ChevronDown, 
  X,
  Lock,
  BarChart3,
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { ClusterApis } from 'apis/clusterAPI';
import { useToast } from 'context/ToastContext';
import { 
  AccessButton, 
  AccessStatusDropdown, 
  getStatusIcon, 
  AccessStatus 
} from 'components/common/AccessStatusButton';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
}));

// Section item interface
interface SectionItem {
  id: string;
  sectionName: string;
  position: number;
  mandatory: boolean;
  isRemoved: boolean;
  clusterResolutions: { [clusterReference: string]: AccessStatus };
}

// Feature group interface
interface FeatureGroup {
  id: string;
  name: string;
  displayName: string;
  sections: SectionItem[];
}

export const TemplatePersonalizationPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  const toast = useToast();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  
  // Product type options
  type ProductType = 'EMAIL' | 'DASHBOARD' | 'HER';
  
  // State
  const [featureGroups, setFeatureGroups] = useState<FeatureGroup[]>([]);
  const [sections, setSections] = useState<SectionItem[]>([]);
  const [removedSections, setRemovedSections] = useState<SectionItem[]>([]);
  const [clusterColumns, setClusterColumns] = useState<string[]>([]);
  const [clustersPerProduct, setClustersPerProduct] = useState<Record<string, string[]>>({}); // Map product -> clusters
  const [availableClusters, setAvailableClusters] = useState<{ clusterReference: string; clusterName: string; clusterId: string }[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<string>('');
  const [selectedProductType, setSelectedProductType] = useState<ProductType>('EMAIL');
  const [productTypeOptions, setProductTypeOptions] = useState<{ value: string; label: string; productType: ProductType }[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [addClusterAnchor, setAddClusterAnchor] = useState<null | HTMLElement>(null);
  const [addSectionDialogOpen, setAddSectionDialogOpen] = useState(false);
  const [masterSectionList, setMasterSectionList] = useState<SectionItem[]>([]);
  const [schemaLevel2Options, setSchemaLevel2Options] = useState<string[]>([]);
  const [selectedMasterSection, setSelectedMasterSection] = useState<string>('');
  const [selectedRemovedSection, setSelectedRemovedSection] = useState<string>('');
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);
  // Track newly added EMAIL sections for push-new-sections API
  // Map of event (e.g., MONTHLY_SUMMARY) -> Set of section names (e.g., HEADER_V4)
  const [newEmailSections, setNewEmailSections] = useState<Map<string, Set<string>>>(new Map());
  
  // Track original state to detect changes
  const [originalSections, setOriginalSections] = useState<SectionItem[]>([]);
  const [originalClustersPerProduct, setOriginalClustersPerProduct] = useState<Record<string, string[]>>({});
  
  const addClusterOpen = Boolean(addClusterAnchor);
  
  // Check if current selection is Dashboard or HER
  const isDashboard = selectedProductType === 'DASHBOARD';
  const isHER = selectedProductType === 'HER';

  // Fetch template personalization data on mount (LEVEL_1)
  useEffect(() => {
    const fetchTemplateData = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        setLoading(true);
        const response = await ClusterApis.getFeatureMetric(rc, selectedPilotId.toString(), 'LEVEL_2');
        
        if (response.success && response.data) {
          const responseData = response.data as any;
          const rawData = responseData.features || responseData.data || responseData;
          
          if (Array.isArray(rawData)) {
            // Build product type options from the data
            const emailProducts = new Map<string, Set<string>>(); // productKey -> Set of fuel types
            const productClusters = new Map<string, Set<string>>(); // productKey -> Set of clusters
            let hasDashboard = false;
            let hasHER = false;

            // Group data by feature prefix and build sections
            const featureMap = new Map<string, FeatureGroup>();
            const sectionsList: SectionItem[] = [];
            
            rawData.forEach((item: any, index: number) => {
              const featureId = item.featureId || item.feature_id || '';
              const featureName = item.featureName || item.feature_name || item.name || '';
              const clusterRef = item.clusterReference || item.cluster_reference || '';
              const resolution = (item.resolution || 'pass').toLowerCase() as 'access' | 'pass' | 'kill';
              const apiPosition = item.position || item.pos || 0; // Get position from API
              
              // Parse feature ID to get feature group (e.g., "EMAIL.SF.MONTHLY_SUMMARY" from "EMAIL.SF.MONTHLY_SUMMARY.HEADER_V4.FALSE.ELECTRIC")
              const parts = featureId.split('.');
              const productType = parts[0]?.toUpperCase();
              const featureGroupKey = parts.slice(0, 3).join('.');
              const fuelType = parts[parts.length - 1]; // Last part is fuel type (ELECTRIC, GAS, WATER)
              
              // Determine the product key for cluster mapping
              let productKey = featureGroupKey;
              if (productType === 'DASHBOARD') {
                productKey = 'DASHBOARD';
              } else if (productType === 'HER') {
                productKey = 'HER';
              }
              
              // Track clusters per product
              if (!productClusters.has(productKey)) {
                productClusters.set(productKey, new Set(['(Default)'])); // Always include Default
              }
              // Add cluster reference, but skip if it's a variant of "default" (already added)
              if (clusterRef && clusterRef.toUpperCase() !== 'DEFAULT') {
                productClusters.get(productKey)!.add(clusterRef);
              }
              
              // Track product types
              if (productType === 'EMAIL') {
                if (!emailProducts.has(productKey)) {
                  emailProducts.set(productKey, new Set());
                }
                if (['ELECTRIC', 'GAS', 'WATER'].includes(fuelType)) {
                  emailProducts.get(productKey)!.add(fuelType);
                }
              } else if (productType === 'DASHBOARD') {
                hasDashboard = true;
              } else if (productType === 'HER') {
                hasHER = true;
              }
              
              // Find or create section
              let existingSection = sectionsList.find(s => s.sectionName === featureId);
              
              if (!existingSection) {
                existingSection = {
                  id: featureId,
                  sectionName: featureId,
                  position: apiPosition, // Use position from API
                  mandatory: parts.includes('TRUE'),
                  isRemoved: false,
                  clusterResolutions: {
                    '(Default)': 'access' // Default column
                  }
                };
                sectionsList.push(existingSection);
              } else {
                // Update position if this item has a valid position and section doesn't have one yet
                if (apiPosition > 0 && existingSection.position === 0) {
                  existingSection.position = apiPosition;
                }
              }
              
              // Update cluster resolution
              if (clusterRef) {
                // Normalize "DEFAULT" to "(Default)"
                const normalizedClusterRef = clusterRef.toUpperCase() === 'DEFAULT' ? '(Default)' : clusterRef;
                existingSection.clusterResolutions[normalizedClusterRef] = resolution;
              }
              
              // Group by feature
              if (!featureMap.has(featureGroupKey)) {
                featureMap.set(featureGroupKey, {
                  id: featureGroupKey,
                  name: featureGroupKey,
                  displayName: getFeatureDisplayName(featureGroupKey),
                  sections: []
                });
              }
            });
            
            // Sort sections by position from API
            sectionsList.sort((a, b) => a.position - b.position);
            
            // Convert productClusters Map to object for state
            const clustersPerProductObj: Record<string, string[]> = {};
            productClusters.forEach((clusters, key) => {
              // Ensure (Default) is always first
              const clusterArray = Array.from(clusters);
              const defaultIndex = clusterArray.indexOf('(Default)');
              if (defaultIndex > 0) {
                clusterArray.splice(defaultIndex, 1);
                clusterArray.unshift('(Default)');
              }
              clustersPerProductObj[key] = clusterArray;
            });
            setClustersPerProduct(clustersPerProductObj);
            
            // Build product type dropdown options
            const options: { value: string; label: string; productType: ProductType }[] = [];
            
            // Add Email products with fuel types
            emailProducts.forEach((fuelTypes, productKey) => {
              const fuelTypesList = Array.from(fuelTypes).map(ft => {
                switch(ft) {
                  case 'ELECTRIC': return 'Electric';
                  case 'GAS': return 'Gas';
                  case 'WATER': return 'Water';
                  default: return ft;
                }
              }).join(', ');
              const displayName = getFeatureDisplayName(productKey);
              options.push({
                value: productKey,
                label: `${displayName} (${fuelTypesList})`,
                productType: 'EMAIL'
              });
            });
            
            // Add Dashboard if present
            if (hasDashboard) {
              options.push({
                value: 'DASHBOARD',
                label: 'DASHBOARD',
                productType: 'DASHBOARD'
              });
            }
            
            // Add HER if present
            if (hasHER) {
              options.push({
                value: 'HER',
                label: 'HER',
                productType: 'HER'
              });
            }
            
            setProductTypeOptions(options);
            setSections(sectionsList);
            setMasterSectionList(sectionsList.map(s => ({ ...s })));
            setFeatureGroups(Array.from(featureMap.values()));
            
            // Store original state for change detection
            setOriginalSections(sectionsList.map(s => ({ ...s })));
            setOriginalClustersPerProduct({ ...clustersPerProductObj });
            
            // Set default selected feature and its clusters
            if (options.length > 0) {
              const firstOption = options[0];
              setSelectedFeature(firstOption.value);
              setSelectedProductType(firstOption.productType);
              
              // Set cluster columns for the first product
              const firstProductClusters = clustersPerProductObj[firstOption.value] || ['(Default)'];
              setClusterColumns(firstProductClusters);
            }
          }
        } else {
          const errorMessage = typeof response.error === 'object' 
            ? (response.error?.message || JSON.stringify(response.error)) 
            : (response.error || 'Failed to fetch template data');
          toast.error(errorMessage);
        }
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching template data:', error);
        toast.error('Failed to fetch template data');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplateData();
  }, [rc, selectedPilotId]);

  // Fetch available clusters for the Add Cluster dropdown
  useEffect(() => {
    const fetchClusters = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        const response = await ClusterApis.getClustersList(rc, selectedPilotId.toString(), 'TEMPLATE');
        
        if (response.success && response.data) {
          const clusters = response.data.map((cluster: any) => ({
            clusterId: cluster.cluster_id || cluster.clusterId || '',
            clusterName: cluster.cluster_name || cluster.clusterName || '',
            clusterReference: cluster.cluster_reference || cluster.clusterReference || '',
          }));
          setAvailableClusters(clusters);
        }
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching clusters:', error);
      }
    };

    fetchClusters();
  }, [rc, selectedPilotId]);

  // Fetch feature metric schema for override section dropdown (email level2)
  useEffect(() => {
    const fetchFeatureMetricSchema = async () => {
      if (!selectedPilotId) {
        return;
      }

      try {
        const response = await ClusterApis.getFeatureMetricSchema(rc, selectedPilotId.toString());
        
        if (response.success && response.data) {
          const schemaData = response.data;
          // Extract email level2 FCMs for the override section dropdown
          if (schemaData.email?.fcms?.level2) {
            setSchemaLevel2Options(schemaData.email.fcms.level2);
          }
        }
      } catch (error) {
        rc.error(rc.getName(TemplatePersonalizationPage), 'Error fetching feature metric schema:', error);
      }
    };

    fetchFeatureMetricSchema();
  }, [rc, selectedPilotId]);

  const getFeatureDisplayName = (featureId: string): string => {
    // Convert feature ID to display name
    const parts = featureId.split('.');
    if (parts.length >= 3) {
      const name = parts[2].replace(/_/g, ' ');
      return `(${parts[1]?.substring(0, 2) || 'EL'}) ${name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()}`;
    }
    return featureId;
  };

  const handleBack = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
  };

  const handleAccessChange = (sectionId: string, clusterReference: string, newStatus: 'access' | 'pass' | 'kill') => {
    // Prevent setting 'pass' for default cluster
    const isDefaultCluster = clusterReference === '(Default)';
    const finalStatus = (isDefaultCluster && newStatus === 'pass') ? 'access' : newStatus;
    
    setSections(prev => prev.map(section => 
      section.id === sectionId 
        ? { 
            ...section, 
            clusterResolutions: { 
              ...section.clusterResolutions, 
              [clusterReference]: finalStatus 
            } 
          }
        : section
    ));
    setHasChanges(true);
  };

  const handleMandatoryToggle = (sectionId: string) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const newMandatory = !section.mandatory;
        // Update the section name - change the second-to-last param (TRUE/FALSE)
        const parts = section.sectionName.split('.');
        if (parts.length >= 2) {
          // Second to last position is the mandatory flag
          parts[parts.length - 2] = newMandatory ? 'TRUE' : 'FALSE';
        }
        const newSectionName = parts.join('.');
        return { 
          ...section, 
          mandatory: newMandatory,
          sectionName: newSectionName,
          id: newSectionName // Update id as well since it's based on section name
        };
      }
      return section;
    }));
    setHasChanges(true);
  };

  const handleRemoveSection = (sectionId: string) => {
    const sectionToRemove = sections.find(s => s.id === sectionId);
    if (sectionToRemove) {
      setSections(prev => prev.filter(s => s.id !== sectionId));
      setRemovedSections(prev => [...prev, { ...sectionToRemove, isRemoved: true }]);
      setHasChanges(true);
    }
  };

  const handleReAddSection = () => {
    if (!selectedRemovedSection) return;
    
    const sectionToAdd = removedSections.find(s => s.id === selectedRemovedSection);
    if (sectionToAdd) {
      setRemovedSections(prev => prev.filter(s => s.id !== selectedRemovedSection));
      setSections(prev => [...prev, { ...sectionToAdd, isRemoved: false, position: prev.length + 1 }]);
      setSelectedRemovedSection('');
      setHasChanges(true);
    }
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, sectionId: string, column: string) => {
    const key = `${sectionId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleDropdownClose = (sectionId: string, column: string) => {
    const key = `${sectionId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: null }));
  };

  // Helper function to detect if a section has changed
  const hasSectionChanged = (currentSection: SectionItem, originalSection: SectionItem | undefined): boolean => {
    // New section (not in original)
    if (!originalSection) {
      return true;
    }
    
    // Check if position changed
    if (currentSection.position !== originalSection.position) {
      return true;
    }
    
    // Check if mandatory changed
    if (currentSection.mandatory !== originalSection.mandatory) {
      return true;
    }
    
    // Check if section name changed (which includes mandatory flag)
    if (currentSection.sectionName !== originalSection.sectionName) {
      return true;
    }
    
    // Check if cluster resolutions changed
    const currentClusters = Object.keys(currentSection.clusterResolutions);
    const originalClusters = Object.keys(originalSection.clusterResolutions);
    
    // Check if clusters were added or removed
    if (currentClusters.length !== originalClusters.length) {
      return true;
    }
    
    // Check if any resolution value changed
    for (const clusterRef of currentClusters) {
      const currentResolution = currentSection.clusterResolutions[clusterRef] || 'pass';
      const originalResolution = originalSection.clusterResolutions[clusterRef] || 'pass';
      if (currentResolution !== originalResolution) {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to detect if clusters changed for a product
  const hasClustersChanged = (productKey: string): boolean => {
    const currentClusters = clustersPerProduct[productKey] || [];
    const originalClusters = originalClustersPerProduct[productKey] || [];
    
    if (currentClusters.length !== originalClusters.length) {
      return true;
    }
    
    // Check if cluster order or membership changed
    const currentSet = new Set(currentClusters);
    const originalSet = new Set(originalClusters);
    
    if (currentSet.size !== originalSet.size) {
      return true;
    }
    
    for (const cluster of currentClusters) {
      if (!originalSet.has(cluster)) {
        return true;
      }
    }
    
    return false;
  };

  // Helper function to get all products that have been modified
  const getModifiedProducts = (): Set<string> => {
    const modifiedProducts = new Set<string>();
    
    // Check sections for changes
    sections.forEach(currentSection => {
      const originalSection = originalSections.find(s => s.id === currentSection.id);
      if (hasSectionChanged(currentSection, originalSection)) {
        // Extract product key from section name
        const parts = currentSection.sectionName.split('.');
        if (parts[0]?.toUpperCase() === 'DASHBOARD') {
          modifiedProducts.add('DASHBOARD');
        } else if (parts.length >= 3) {
          modifiedProducts.add(parts.slice(0, 3).join('.'));
        }
      }
    });
    
    // Check for removed sections
    originalSections.forEach(originalSection => {
      const currentSection = sections.find(s => s.id === originalSection.id);
      if (!currentSection) {
        // Section was removed
        const parts = originalSection.sectionName.split('.');
        if (parts[0]?.toUpperCase() === 'DASHBOARD') {
          modifiedProducts.add('DASHBOARD');
        } else if (parts.length >= 3) {
          modifiedProducts.add(parts.slice(0, 3).join('.'));
        }
      }
    });
    
    // Check for cluster changes per product
    Object.keys(clustersPerProduct).forEach(productKey => {
      if (hasClustersChanged(productKey)) {
        modifiedProducts.add(productKey);
      }
    });
    
    return modifiedProducts;
  };

  const handleSaveChanges = async () => {
    if (!selectedPilotId) {
      toast.error('No project selected');
      return;
    }

    setSaving(true);

    try {
      // Get all modified products
      const modifiedProducts = getModifiedProducts();
      
      if (modifiedProducts.size === 0) {
        toast.warning('No changes detected');
        setSaving(false);
        return;
      }
      
      // Group sections by component (EMAIL, DASHBOARD) based on sectionName prefix
      // Note: HER is excluded from feature resolution for this screen
      const componentGroups: { [key: string]: SectionItem[] } = {
        EMAIL: [],
        DASHBOARD: []
      };

      // Check if any EMAIL product is modified
      const hasAnyEmailProductModified = Array.from(modifiedProducts).some(productKey => 
        productKey.startsWith('EMAIL.')
      );
      // Check if DASHBOARD is modified
      const hasDashboardModified = modifiedProducts.has('DASHBOARD');

      // Include sections based on component-level modification
      // If ANY email product is dirty, include ALL email product sheets
      // If dashboard is dirty, include ALL dashboard sheets
      sections.forEach(section => {
        const prefix = section.sectionName.split('.')[0].toUpperCase();
        if (prefix in componentGroups) {
          if (prefix === 'EMAIL' && hasAnyEmailProductModified) {
            // Include all EMAIL sections if any EMAIL product is modified
            componentGroups[prefix].push(section);
          } else if (prefix === 'DASHBOARD' && hasDashboardModified) {
            // Include all DASHBOARD sections if DASHBOARD is modified
            componentGroups[prefix].push(section);
          }
        }
      });

      // Build files array for each component that has modified sections
      const files: {
        component: 'EMAIL' | 'DASHBOARD';
        level: 'L1' | 'L2';
        headers: string[];
        features: { featureId: string; values: string[] }[];
      }[] = [];

      (['EMAIL', 'DASHBOARD'] as const).forEach(component => {
        const componentSections = componentGroups[component];
        if (componentSections.length > 0) {
          // Get all clusters for ALL products of this component type
          // Since we're including all sections of the component, we need clusters from all products
          const componentClusters = new Set<string>();
          
          // Collect clusters from all products of this component type
          Object.keys(clustersPerProduct).forEach(productKey => {
            const productPrefix = productKey.split('.')[0].toUpperCase();
            if ((component === 'EMAIL' && productPrefix === 'EMAIL') || 
                (component === 'DASHBOARD' && productKey === 'DASHBOARD')) {
              const productClusters = clustersPerProduct[productKey] || ['(Default)'];
              productClusters.forEach(cluster => componentClusters.add(cluster));
            }
          });
          
          // Convert to array and ensure (Default) is first
          const allClusters = Array.from(componentClusters);
          const defaultIndex = allClusters.indexOf('(Default)');
          if (defaultIndex > 0) {
            allClusters.splice(defaultIndex, 1);
            allClusters.unshift('(Default)');
          }
          
          // Build headers array from clusters
          const apiClusterColumns = allClusters.map(c => c === '(Default)' ? 'DEFAULT' : c);
          const headers = ['FEATURE_ID', ...apiClusterColumns, 'POSITION'];
          
          // Sort sections by product (first 3 parts of sectionName) and then by position
          // This groups all features of a single product together
          const sortedSections = [...componentSections].sort((a, b) => {
            const productA = a.sectionName.split('.').slice(0, 3).join('.');
            const productB = b.sectionName.split('.').slice(0, 3).join('.');
            // First sort by product name
            if (productA !== productB) {
              return productA.localeCompare(productB);
            }
            // Then sort by position within the same product
            return a.position - b.position;
          });

          files.push({
            component,
            level: 'L2',
            headers,
            features: sortedSections.map(section => ({
              featureId: section.id,
              values: [
                ...allClusters.map(clusterRef => {
                  const resolution = section.clusterResolutions[clusterRef] || 'pass';
                  return resolution.toUpperCase();
                }),
                // Add position value for both EMAIL and DASHBOARD
                section.position.toString()
              ]
            }))
          });
        }
      });

      if (files.length === 0) {
        toast.error('No sections to save');
        setSaving(false);
        return;
      }

      const payload = {
        projectId: selectedPilotId.toString(),
        files
      };

      const response = await ClusterApis.saveFeatureAccess(rc, payload);

      if (response.success && response.data) {
        const responseData = response.data;
        
        // Check if all files were uploaded and API was notified
        const uploadedFiles = responseData.uploaded_files || [];
        const allNotified = uploadedFiles.every((file: any) => file.api_notified);
        
        if (allNotified) {
          toast.success(responseData.message || 'Template configuration saved successfully');
        } else {
          // Some files may have notification errors
          const failedFiles = uploadedFiles.filter((file: any) => !file.api_notified);
          const failedNames = failedFiles.map((f: any) => f.file_name).join(', ');
          toast.warning(`Template configuration saved, but some files failed to notify backend: ${failedNames}`);
        }

        // Show any additional errors
        if (responseData.errors && responseData.errors.length > 0) {
          responseData.errors.forEach((error: any) => {
            const errorMessage = typeof error === 'object' ? (error.message || JSON.stringify(error)) : error;
            toast.error(errorMessage);
          });
        }

        // Call push-new-sections API if there are new EMAIL sections
        if (newEmailSections.size > 0) {
          const emailPayload: { event: string; sections: string[] }[] = [];
          
          newEmailSections.forEach((sectionsSet, event) => {
            if (sectionsSet.size > 0) {
              emailPayload.push({
                event,
                sections: Array.from(sectionsSet)
              });
            }
          });

          if (emailPayload.length > 0) {
            try {
              const pushResponse = await ClusterApis.pushNewSections(rc, {
                project_id: selectedPilotId.toString(),
                email: emailPayload
              });

              if (pushResponse.success) {
                rc.status(rc.getName(TemplatePersonalizationPage), 'New sections pushed successfully');
              } else {
                rc.warn(rc.getName(TemplatePersonalizationPage), 'Failed to push new sections:', pushResponse.error);
              }
            } catch (pushError) {
              rc.error(rc.getName(TemplatePersonalizationPage), 'Error pushing new sections:', pushError);
            }
          }

          // Clear tracked new sections after save
          setNewEmailSections(new Map());
        }

        // Update original state to reflect the saved state
        setOriginalSections(sections.map(s => ({ ...s })));
        setOriginalClustersPerProduct({ ...clustersPerProduct });
        
        setHasChanges(false);
      } else {
        // Handle error object with code/message structure
        const errorMessage = typeof response.error === 'object' 
          ? (response.error?.message || JSON.stringify(response.error)) 
          : (response.error || 'Failed to save template configuration');
        toast.error(errorMessage);
      }
    } catch (error) {
      rc.error(rc.getName(TemplatePersonalizationPage), 'Error saving template configuration:', error);
      toast.error('Failed to save template configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddClusterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddClusterAnchor(event.currentTarget);
  };

  const handleAddClusterClose = () => {
    setAddClusterAnchor(null);
  };

  const handleClusterTypeSelect = (clusterReference: string) => {
    if (clusterColumns.includes(clusterReference)) {
      handleAddClusterClose();
      return;
    }

    setClusterColumns(prev => [...prev, clusterReference]);
    
    // Update clustersPerProduct for ALL products of the same component type (EMAIL or DASHBOARD)
    // This ensures the cluster is visible across all products of that type
    setClustersPerProduct(prev => {
      const updated = { ...prev };
      
      // Determine the component type (EMAIL or DASHBOARD)
      const componentType = selectedProductType; // 'EMAIL' or 'DASHBOARD'
      
      // Update all products that belong to this component type
      Object.keys(updated).forEach(productKey => {
        const productPrefix = productKey.split('.')[0].toUpperCase();
        if (productPrefix === componentType || productKey === componentType) {
          updated[productKey] = [...(updated[productKey] || ['(Default)']), clusterReference];
        }
      });
      
      // Also update the current selected feature if not already updated
      if (!updated[selectedFeature]?.includes(clusterReference)) {
        updated[selectedFeature] = [...(updated[selectedFeature] || ['(Default)']), clusterReference];
      }
      
      return updated;
    });

    // Set default 'pass' resolution for all sections of the same component type
    setSections(prev => prev.map(section => {
      const sectionComponentType = section.sectionName.split('.')[0].toUpperCase();
      // Only update sections that belong to the same component type (EMAIL or DASHBOARD)
      if (sectionComponentType === selectedProductType || section.sectionName.startsWith(selectedProductType)) {
        return {
          ...section,
          clusterResolutions: {
            ...section.clusterResolutions,
            [clusterReference]: 'pass'
          }
        };
      }
      return section;
    }));

    setHasChanges(true);
    handleAddClusterClose();
  };

  const handleRemoveCluster = (clusterReference: string) => {
    // Remove the cluster column
    setClusterColumns(prev => prev.filter(col => col !== clusterReference));
    
    // Update clustersPerProduct for ALL products of the same component type
    setClustersPerProduct(prev => {
      const updated = { ...prev };
      const componentType = selectedProductType;
      
      Object.keys(updated).forEach(productKey => {
        const productPrefix = productKey.split('.')[0].toUpperCase();
        if (productPrefix === componentType || productKey === componentType) {
          updated[productKey] = (updated[productKey] || []).filter(c => c !== clusterReference);
        }
      });
      
      return updated;
    });

    // Remove the cluster resolution from all sections of the same component type
    setSections(prev => prev.map(section => {
      const sectionComponentType = section.sectionName.split('.')[0].toUpperCase();
      if (sectionComponentType === selectedProductType || section.sectionName.startsWith(selectedProductType)) {
        const { [clusterReference]: removed, ...remainingResolutions } = section.clusterResolutions;
        return {
          ...section,
          clusterResolutions: remainingResolutions
        };
      }
      return section;
    }));

    setHasChanges(true);
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, sectionId: string) => {
    setDraggedItem(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, sectionId: string) => {
    e.preventDefault();
    if (draggedItem !== sectionId) {
      setDragOverItem(sectionId);
    }
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    
    if (!draggedItem || draggedItem === targetSectionId) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    setSections(prev => {
      const newSections = [...prev];
      const draggedIndex = newSections.findIndex(s => s.id === draggedItem);
      const targetIndex = newSections.findIndex(s => s.id === targetSectionId);
      
      if (draggedIndex === -1 || targetIndex === -1) return prev;

      const draggedSection = newSections[draggedIndex];
      const targetSection = newSections[targetIndex];
      
      // Get the product key based on component type
      // For DASHBOARD: use first part (DASHBOARD)
      // For EMAIL: use first 3 parts (EMAIL.SF.MONTHLY_SUMMARY)
      const getProductKey = (sectionName: string) => {
        const parts = sectionName.split('.');
        if (parts[0]?.toUpperCase() === 'DASHBOARD') {
          return 'DASHBOARD';
        }
        return parts.slice(0, 3).join('.');
      };
      
      const draggedProductKey = getProductKey(draggedSection.sectionName);
      const targetProductKey = getProductKey(targetSection.sectionName);
      
      // Only allow reordering within the same product
      if (draggedProductKey !== targetProductKey) {
        return prev;
      }

      // Remove dragged item and insert at target position
      const [removed] = newSections.splice(draggedIndex, 1);
      newSections.splice(targetIndex, 0, removed);

      // Reassign positions only for this product (1, 2, 3, ...)
      let positionCounter = 1;
      return newSections.map(section => {
        if (getProductKey(section.sectionName) === draggedProductKey) {
          return { ...section, position: positionCounter++ };
        }
        return section;
      });
    });

    setDraggedItem(null);
    setDragOverItem(null);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleAddSectionFromMaster = () => {
    if (!selectedMasterSection) return;
    
    // Check if it's from schema level2 options
    if (schemaLevel2Options.includes(selectedMasterSection)) {
      // Create a new section from the schema level2 option
      const newSection: SectionItem = {
        id: selectedMasterSection,
        sectionName: selectedMasterSection,
        position: sections.length + 1,
        mandatory: selectedMasterSection.includes('.TRUE.'),
        isRemoved: false,
        clusterResolutions: {
          '(Default)': 'access'
        }
      };
      
      // Add resolutions for existing clusters
      clusterColumns.forEach(clusterRef => {
        if (clusterRef !== '(Default)') {
          newSection.clusterResolutions[clusterRef] = 'pass';
        }
      });
      
      setSections(prev => [...prev, newSection]);
      
      // Track new EMAIL sections for push-new-sections API
      // Format: EMAIL.SF.MONTHLY_SUMMARY.TRUE.HEADER_V4.ELECTRIC
      // We need: event = MONTHLY_SUMMARY, section = HEADER_V4
      if (selectedMasterSection.startsWith('EMAIL')) {
        const parts = selectedMasterSection.split('.');
        // parts: [EMAIL, SF, MONTHLY_SUMMARY, ..., SECTION_NAME, TRUE/FALSE, FUEL_TYPE]
        // Section name is third last item
        if (parts.length >= 5) {
          const event = parts[2]; // e.g., MONTHLY_SUMMARY
          const sectionName = parts[parts.length - 3]; // e.g., HEADER_V4 (third last)
          
          setNewEmailSections(prev => {
            const updated = new Map(prev);
            if (!updated.has(event)) {
              updated.set(event, new Set());
            }
            updated.get(event)!.add(sectionName);
            return updated;
          });
        }
      }
      
      setSelectedMasterSection('');
      setAddSectionDialogOpen(false);
      setHasChanges(true);
      return;
    }
    
    // Fallback: check master section list
    const masterSection = masterSectionList.find(s => s.id === selectedMasterSection);
    if (masterSection && !sections.find(s => s.id === masterSection.id)) {
      setSections(prev => [...prev, { 
        ...masterSection, 
        position: prev.length + 1,
        isRemoved: false 
      }]);
      
      // Track new EMAIL sections for push-new-sections API
      if (masterSection.sectionName.startsWith('EMAIL')) {
        const parts = masterSection.sectionName.split('.');
        if (parts.length >= 5) {
          const event = parts[2];
          const sectionName = parts[parts.length - 3]; // Third last item
          
          setNewEmailSections(prev => {
            const updated = new Map(prev);
            if (!updated.has(event)) {
              updated.set(event, new Set());
            }
            updated.get(event)!.add(sectionName);
            return updated;
          });
        }
      }
      
      setSelectedMasterSection('');
      setAddSectionDialogOpen(false);
      setHasChanges(true);
    }
  };

  // Filter out clusters that are already added as columns
  // Handle (Default) vs DEFAULT mismatch - clusterColumns uses (Default) but API returns DEFAULT
  const availableClusterOptions = availableClusters.filter(
    cluster => {
      const clusterRef = cluster.clusterReference;
      const isAlreadyAdded = clusterColumns.includes(clusterRef) || 
                            (clusterRef === 'DEFAULT' && clusterColumns.includes('(Default)'));
      return !isAlreadyAdded;
    }
  );

  // Filter sections by selected feature/product, sort by original position, then assign display positions
  const filteredSections = (selectedFeature 
    ? sections.filter(section => {
        // For DASHBOARD and HER, filter by product type prefix
        if (selectedFeature === 'DASHBOARD' || selectedFeature === 'HER') {
          return section.sectionName.startsWith(selectedFeature);
        }
        // For EMAIL products, filter by the full feature prefix
        return section.sectionName.startsWith(selectedFeature);
      })
    : sections
  )
    .sort((a, b) => a.position - b.position) // Sort by original API position
    .map((section, index) => ({
      ...section,
      position: index + 1, // Assign sequential display position starting from 1
    }));

  // Extract fuel types present in the filtered sections (what's shown in the table)
  const tableFuelTypes = new Set<string>();
  filteredSections.forEach(section => {
    const parts = section.sectionName.split('.');
    const fuelType = parts[parts.length - 1]; // Last part is fuel type (ELECTRIC, GAS, WATER)
    if (['ELECTRIC', 'GAS', 'WATER'].includes(fuelType)) {
      tableFuelTypes.add(fuelType);
    }
  });

  // Get sections not currently in the table from schema level2 options
  // Filter by selected product/feature prefix, fuel type, and exclude sections already in table
  const availableOverrideSections = schemaLevel2Options.filter(option => {
    // First check if option is already in the filtered sections (what's shown in the table)
    if (filteredSections.find(s => s.id === option || s.sectionName === option)) {
      return false;
    }
    
    // Filter by selected product
    if (selectedFeature) {
      // For EMAIL products, filter by the full feature prefix (e.g., EMAIL.CDF.NEIGHBOURHOOD_COMPARISON)
      if (selectedFeature.startsWith('EMAIL')) {
        if (!option.startsWith(selectedFeature)) {
          return false;
        }
        // Also filter by fuel type - only show options with fuel types present in the table
        const optionParts = option.split('.');
        const optionFuelType = optionParts[optionParts.length - 1];
        if (['ELECTRIC', 'GAS', 'WATER'].includes(optionFuelType) && tableFuelTypes.size > 0) {
          return tableFuelTypes.has(optionFuelType);
        }
        return true;
      }
      // For DASHBOARD and HER, filter by product type prefix
      return option.startsWith(selectedFeature);
    }
    
    return true;
  });

  // Get sections not currently in the table (legacy - for fallback)
  const availableMasterSections = masterSectionList.filter(
    ms => !filteredSections.find(s => s.id === ms.id)
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Template Personalization
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Back to Cluster Configuration
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mx: 'auto' }}>
        <StyledCard>
          <CardContent sx={{ p: 3, textAlign: 'left' }}>
            {/* Template Configuration Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: .5 }}>
                  <Lock size={24} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Template Configuration
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Define and apply template configurations to clusters.
                </Typography>
              </Box>
            </Box>

            {/* Filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 350 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: .5 }}>
                  Filter by Feature
                </Typography>
                <Select
                  value={selectedFeature}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedFeature(value);
                    // Find the product type for this selection
                    const option = productTypeOptions.find(opt => opt.value === value);
                    if (option) {
                      setSelectedProductType(option.productType);
                    }
                    // Update cluster columns for the selected product
                    const productClusters = clustersPerProduct[value] || ['(Default)'];
                    setClusterColumns(productClusters);
                  }}
                >
                  {productTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{p: 3, border: `1px dashed ${theme.palette.divider}`, borderRadius: 2 }}>

            {/* Template Section */}
            {selectedFeature && (
              <Box sx={{ mb: 3}}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BarChart3 size={20} style={{ color: theme.palette.primary.main }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                      {isDashboard ? 'Dashboard Template' : isHER ? 'HER Template' : 'Email Template for:'}{' '}
                      {!isDashboard && !isHER && (
                        <Typography component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          {getFeatureDisplayName(selectedFeature)}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {!isDashboard && !isHER && (
                      <Button
                        variant="outlined"
                        startIcon={<BarChart3 size={16} />}
                        onClick={() => setAddSectionDialogOpen(true)}
                        sx={{ color: 'text.secondary', borderColor: 'divider' }}
                      >
                        Add New Section
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<Plus size={16} />}
                      endIcon={<ChevronDown size={16} />}
                      onClick={handleAddClusterClick}
                      sx={{ color: 'text.secondary', borderColor: 'divider' }}
                    >
                      Add Cluster
                    </Button>
                    <Menu
                      id="add-cluster-menu"
                      anchorEl={addClusterAnchor}
                      open={addClusterOpen}
                      onClose={handleAddClusterClose}
                      PaperProps={{
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                          border: '1px solid #E0E0E0',
                          minWidth: '160px',
                        },
                      }}
                    >
                      {availableClusterOptions.length > 0 ? (
                        availableClusterOptions.map((cluster) => (
                          <MenuItem 
                            key={cluster.clusterId || cluster.clusterReference} 
                            onClick={() => handleClusterTypeSelect(cluster.clusterReference)}
                          >
                            {cluster.clusterReference || cluster.clusterName}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          {availableClusters.length === 0 ? 'Loading clusters...' : 'All clusters added'}
                        </MenuItem>
                      )}
                    </Menu>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  {isHER 
                    ? 'HER template configuration is available in the next update.' 
                    : isDashboard 
                      ? 'This table shows the dashboard sections and their status across different clusters.'
                      : 'This table shows the email sections and their status across different clusters.'
                  }
                </Typography>
              </Box>
            )}

            {/* Template Sections Table */}
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                py: 8,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                  Loading template data...
                </Typography>
              </Box>
            ) : isHER ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  Available in next update
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  HER template personalization will be available in the next update.
                </Typography>
              </Box>
            ) : filteredSections.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8, 
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                bgcolor: 'background.default'
              }}>
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  No sections found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  No template sections available for this configuration.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ 
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                border: `1px solid ${theme.palette.divider}`,
                overflowX: 'auto',
              }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'background.default' }}>
                      <TableCell sx={{ fontWeight: 600, width: 60 }}>Move</TableCell>
                      {!isDashboard && <TableCell sx={{ fontWeight: 600, width: 80 }}>Remove</TableCell>}
                      {!isDashboard && <TableCell sx={{ fontWeight: 600, width: 100 }}>Mandatory</TableCell>}
                      <TableCell sx={{ fontWeight: 600, width: 60 }}>Pos</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Section Name</TableCell>
                      {clusterColumns.map((clusterRef, index) => {
                        const isDefaultCluster = index === 0 || clusterRef === '(Default)';
                        return (
                          <TableCell 
                            key={clusterRef} 
                            align="center" 
                            sx={{ 
                              fontWeight: 600,
                              '&:hover .remove-column-btn': {
                                opacity: 1,
                              },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {clusterRef}
                              {!isDefaultCluster && (
                                <Tooltip title="Remove column">
                                  <IconButton
                                    className="remove-column-btn"
                                    size="small"
                                    onClick={() => handleRemoveCluster(clusterRef)}
                                    sx={{
                                      opacity: 0,
                                      transition: 'opacity 0.2s',
                                      p: 0.5,
                                      bgcolor: 'primary.main',
                                      color: 'error.main',
                                      '&:hover': {
                                        bgcolor: 'primary.dark',
                                      },
                                    }}
                                  >
                                    <X size={14} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSections.map((section) => (
                      <TableRow 
                        key={section.id} 
                        hover
                        draggable
                        onDragStart={(e) => handleDragStart(e, section.id)}
                        onDragOver={(e) => handleDragOver(e, section.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, section.id)}
                        onDragEnd={handleDragEnd}
                        sx={{
                          opacity: draggedItem === section.id ? 0.5 : 1,
                          backgroundColor: dragOverItem === section.id ? 'action.hover' : 'inherit',
                          transition: 'background-color 0.2s',
                        }}
                      >
                        {/* Move column */}
                        <TableCell sx={{ px: 1, py: 0.5 }}>
                          <IconButton 
                            size="small" 
                            sx={{ 
                              cursor: 'grab', 
                              color: 'text.secondary',
                              '&:active': { cursor: 'grabbing' }
                            }}
                          >
                            <DragIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        {/* Remove column - hidden for Dashboard */}
                        {!isDashboard && (
                          <TableCell sx={{ px: 1, py: 0.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleRemoveSection(section.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <X size={16} />
                            </IconButton>
                          </TableCell>
                        )}
                        {/* Mandatory column - hidden for Dashboard */}
                        {!isDashboard && (
                          <TableCell sx={{ px: 1, py: 0.5 }}>
                            <Switch
                              checked={section.mandatory}
                              onChange={() => handleMandatoryToggle(section.id)}
                              size="small"
                              sx={{
                                width: 38,
                                height: 22,
                                padding: 0,
                                '& .MuiSwitch-switchBase': {
                                  padding: 0,
                                  margin: '2px',
                                  transitionDuration: '300ms',
                                  '&.Mui-checked': {
                                    transform: 'translateX(16px)',
                                    color: '#fff',
                                    '& + .MuiSwitch-track': {
                                      backgroundColor: theme.palette.primary.main,
                                      opacity: 1,
                                      border: 0,
                                    },
                                    '& .MuiSwitch-thumb': {
                                      color: '#fff',
                                    },
                                  },
                                  '&.Mui-focusVisible .MuiSwitch-thumb': {
                                    color: theme.palette.primary.main,
                                    border: '6px solid #fff',
                                  },
                                },
                                '& .MuiSwitch-thumb': {
                                  boxSizing: 'border-box',
                                  width: 18,
                                  height: 18,
                                  color: '#fff',
                                },
                                '& .MuiSwitch-track': {
                                  borderRadius: 22 / 2,
                                backgroundColor: theme.palette.mode === 'dark' 
                                  ? theme.palette.grey[700] 
                                  : theme.palette.grey[300],
                                opacity: 1,
                                transition: theme.transitions.create(['background-color'], {
                                  duration: 500,
                                }),
                              },
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{ px: 1, py: 0.5 }}>
                          <Typography sx={{ fontSize: '12px', textAlign: 'center' }}>
                            {section.position}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1, py: 0.5 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
                            {section.sectionName}
                          </Typography>
                        </TableCell>
                        {clusterColumns.map((clusterRef) => {
                          const isDefaultCluster = clusterRef === '(Default)';
                          let resolution = section.clusterResolutions[clusterRef] || (isDefaultCluster ? 'access' : 'pass');
                          
                          // Ensure default cluster never has 'pass' status
                          if (isDefaultCluster && resolution === 'pass') {
                            resolution = 'access';
                          }
                          
                          return (
                            <TableCell key={clusterRef} align="center" sx={{ px: 1, py: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <AccessButton
                                  status={resolution}
                                  onClick={(e) => handleDropdownOpen(e, section.id, clusterRef)}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                                    {getStatusIcon(resolution)}
                                    {resolution.toUpperCase()}
                                  </Box>
                                  <ChevronDown size={14} />
                                </AccessButton>
                              </Box>
                              <AccessStatusDropdown
                                status={resolution}
                                anchorEl={dropdownAnchor[`${section.id}-${clusterRef}`]}
                                onClose={() => handleDropdownClose(section.id, clusterRef)}
                                onSelect={(newStatus) => handleAccessChange(section.id, clusterRef, newStatus)}
                                allowedStatuses={isDefaultCluster ? ['access', 'kill'] : undefined}
                              />
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Add Removed Section */}
            {removedSections.length > 0 && (
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Add Removed Section
                </Typography>
                <FormControl size="small" sx={{ minWidth: 300 }}>
                  <Select
                    value={selectedRemovedSection}
                    onChange={(e) => setSelectedRemovedSection(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select a section to re-add...
                    </MenuItem>
                    {removedSections.map((section) => (
                      <MenuItem key={section.id} value={section.id}>
                        {section.sectionName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  onClick={handleReAddSection}
                  disabled={!selectedRemovedSection}
                  sx={{ color: 'primary.main', borderColor: 'primary.main' }}
                >
                  Re-add
                </Button>
              </Box>
            )}

            </Box>

            {/* Save Changes Button */}
            {hasChanges && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <BglPrimaryButton
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={handleSaveChanges}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </BglPrimaryButton>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>

      {/* Add Override Section Dialog */}
      <Dialog 
        open={addSectionDialogOpen} 
        onClose={() => setAddSectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Override Section
          </Typography>
          <IconButton onClick={() => setAddSectionDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Select a section from the email level2 list to add it to the current template configuration.
          </Typography>
          <FormControl fullWidth>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Override Section
            </Typography>
            <Select
              value={selectedMasterSection}
              onChange={(e) => setSelectedMasterSection(e.target.value)}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 350,
                    '& .MuiMenuItem-root': {
                      fontSize: 12,
                    },
                  },
                },
              }}
            >
              <MenuItem value="" disabled sx={{ fontSize: 14 }}>
                Select a section to add as an override...
              </MenuItem>
              {availableOverrideSections.length > 0 ? (
                availableOverrideSections.map((sectionName) => (
                  <MenuItem key={sectionName} value={sectionName} sx={{ fontSize: 14 }}>
                    {sectionName}
                  </MenuItem>
                ))
              ) : (
                availableMasterSections.map((section) => (
                  <MenuItem key={section.id} value={section.id} sx={{ fontSize: 14 }}>
                    {section.sectionName}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setAddSectionDialogOpen(false)} sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<Plus size={16} />}
            onClick={handleAddSectionFromMaster}
            disabled={!selectedMasterSection}
          >
            Add Section
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
